import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X, Sparkles, Info, AlertTriangle, Megaphone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { changelogAPI } from '../services/api';
import type { ChangelogEntry, ChangelogSeverity } from '../types';

const SEVERITY_STYLE: Record<
  ChangelogSeverity,
  { stripe: string; text: string; icon: React.ComponentType<{ className?: string }>; label: string }
> = {
  info: {
    stripe: 'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-900/60 text-blue-900 dark:text-blue-200',
    text: 'text-blue-900 dark:text-blue-100',
    icon: Info,
    label: 'Update',
  },
  'new-feature': {
    stripe: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900/60 text-emerald-900 dark:text-emerald-200',
    text: 'text-emerald-900 dark:text-emerald-100',
    icon: Sparkles,
    label: 'New',
  },
  important: {
    stripe: 'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-900/60 text-indigo-900 dark:text-indigo-200',
    text: 'text-indigo-900 dark:text-indigo-100',
    icon: Megaphone,
    label: 'Important',
  },
  warning: {
    stripe: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900/60 text-amber-900 dark:text-amber-200',
    text: 'text-amber-900 dark:text-amber-100',
    icon: AlertTriangle,
    label: 'Heads up',
  },
};

const dismissKey = (entryId: string, userId: string | undefined) =>
  `changelog_dismissed_${entryId}_${userId || 'anon'}`;

const ChangelogBanner: React.FC = () => {
  const { user } = useAuth();
  // There is ONE banner per build (main / shopify), and the backend resolves
  // which build you are from the workspace behind `X-Workspace-ID`. That
  // header is injected by the axios interceptor straight from localStorage
  // (`services/api.ts`), NOT from this context — so the workspace has to be
  // resolved before the request goes out, or the backend falls back to
  // `build="main"` and a Shopify merchant is served the Main banner.
  const { activeWorkspace, loading: workspaceLoading } = useWorkspace();

  const [entry, setEntry] = React.useState<ChangelogEntry | null>(null);
  const [hidden, setHidden] = React.useState(false);

  React.useEffect(() => {
    // Wait for the workspace to resolve before asking. On a cold load
    // `active_workspace_id` isn't in localStorage yet, so firing now would
    // race WorkspaceContext's own fetch and lose — most visibly right after
    // a Shopify install, where `InstallComplete` deliberately clears the key
    // and hard-reloads into /dashboard. Anonymous / workspace-less users
    // settle at `loading === false` with a null workspace and correctly get
    // the main-build banner.
    if (workspaceLoading) return;

    let cancelled = false;
    changelogAPI
      .getActive()
      .then((res) => {
        if (cancelled) return;
        const e = res.data?.entry ?? null;
        if (!e) {
          setEntry(null);
          return;
        }
        try {
          if (localStorage.getItem(dismissKey(e.id, user?.id)) === '1') {
            setEntry(null);
            return;
          }
        } catch {
          /* localStorage unavailable — render anyway */
        }
        setEntry(e);
        // Reaching here means this entry is NOT dismissed (the check above
        // returns early when it is), so clearing the session-local hide is
        // safe and never resurrects a dismissed banner. Without it, a user
        // who dismissed their old workspace's banner would see nothing at
        // all after switching — `hidden` would still be true from the
        // previous entry and would swallow the refetch.
        setHidden(false);
      })
      .catch(() => setEntry(null));
    return () => {
      cancelled = true;
    };
    // `activeWorkspace?.id` is the load-bearing dep: it re-resolves the
    // build both when the workspace first lands and when the user switches
    // to one with a different billing provider.
  }, [user?.id, activeWorkspace?.id, workspaceLoading]);

  if (!entry || hidden) return null;

  const style = SEVERITY_STYLE[entry.severity] ?? SEVERITY_STYLE.info;
  const Icon = style.icon;

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissKey(entry.id, user?.id), '1');
    } catch {
      /* localStorage unavailable — still hide for this session */
    }
    setHidden(true);
  };

  return (
    <div
      className={`border-b ${style.stripe} px-4 py-2 sm:py-2.5`}
      role="status"
      aria-label={`${style.label}: ${entry.title}`}
    >
      <div className="max-w-7xl mx-auto flex items-center gap-2 sm:gap-3 text-sm">
        <Icon className={`w-4 h-4 shrink-0 ${style.text}`} aria-hidden="true" />
        <div className={`flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-2 ${style.text}`}>
          <span className="font-semibold truncate">{entry.title}</span>
          <span className="hidden sm:inline opacity-60">·</span>
          <span className="truncate opacity-90">{entry.short_body}</span>
        </div>
        <Link
          to={`/changelog/${entry.slug}`}
          className={`hidden sm:inline-flex items-center gap-1 text-xs font-semibold whitespace-nowrap hover:underline ${style.text}`}
        >
          Read more
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        <Link
          to={`/changelog/${entry.slug}`}
          className={`sm:hidden text-xs font-semibold whitespace-nowrap hover:underline ${style.text}`}
          aria-label="Read more"
        >
          More
        </Link>
        {entry.allow_dismiss && (
          <button
            onClick={handleDismiss}
            className={`p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors ${style.text}`}
            aria-label="Dismiss changelog banner"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ChangelogBanner;
